// firestoreUtils.js
import { doc, setDoc, getDoc } from "firebase/firestore";
import { db, auth } from "./firebase";

/**
 * Salva o resultado de um teste no Firestore.
 *
 * @param {string} testId - ID único do teste.
 * @param {number} score - Pontuação do teste (0-100).
 * @param {number} correctAnswers - Número de respostas corretas.
 * @param {number} totalQuestions - Total de questões no teste.
 * @returns {Promise<{success: boolean, message: string}>} Resultado da operação.
 */
export const saveTestResult = async (testId, score, correctAnswers, totalQuestions) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.error("❌ Usuário não autenticado.");
      return { success: false, message: "Usuário não autenticado." };
    }

    if (!validateInputs(testId, score, correctAnswers, totalQuestions)) {
      return { success: false, message: "Parâmetros inválidos." };
    }

    const userId = user.uid;
    const docRef = doc(db, "testResults", `${userId}_${testId}`);
    const existingDoc = await getDoc(docRef);
    
    let existingData = null;
    if (existingDoc.exists()) {
      existingData = existingDoc.data();

      if (score <= existingData.score) {
        console.warn("⚠️ O novo resultado não é melhor que o existente. Não será sobrescrito.");
        return { success: false, message: "O novo resultado não superou o anterior." };
      }

      console.log("🔄 Novo resultado é melhor. Sobrescrevendo...");
    }

    const resultData = {
      userId,
      testId,
      score,
      correctAnswers,
      totalQuestions,
      date: existingData ? existingData.date : new Date().toISOString(), // Mantém a data original se existir
      updatedAt: new Date().toISOString(), // Marca a última atualização
    };

    await setDoc(docRef, resultData, { merge: true });

    console.log("✅ Resultado do teste salvo com sucesso:", resultData);
    return { success: true, message: "Resultado salvo com sucesso." };
  } catch (error) {
    console.error("🔥 Erro ao salvar resultado do teste:", error);
    return { success: false, message: `Erro ao salvar: ${error.message}` };
  }
};

/**
 * Valida os parâmetros fornecidos para salvar o resultado do teste.
 *
 * @param {string} testId - ID do teste.
 * @param {number} score - Pontuação do teste.
 * @param {number} correctAnswers - Número de respostas corretas.
 * @param {number} totalQuestions - Total de questões no teste.
 * @returns {boolean} Retorna true se todos os parâmetros forem válidos.
 */
const validateInputs = (testId, score, correctAnswers, totalQuestions) => {
  if (!testId || typeof testId !== "string") {
    console.error("❌ ID do teste inválido:", testId);
    return false;
  }

  if (typeof score !== "number" || score < 0 || score > 100) {
    console.error("❌ Pontuação inválida:", score);
    return false;
  }

  if (
    typeof correctAnswers !== "number" ||
    typeof totalQuestions !== "number" ||
    totalQuestions <= 0 ||
    correctAnswers < 0 ||
    correctAnswers > totalQuestions
  ) {
    console.error("❌ Dados de respostas inválidos:", { correctAnswers, totalQuestions });
    return false;
  }

  return true;
};
